import { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { REQUESTS_CHANGED_EVENT } from './PlatformLayout';
import { errorMessage, formatDateTime, planLabel, platformApi } from './platformApi';

const TYPE_LABEL: Record<string, string> = {
  upgrade: 'Upgrade',
  renew: 'Renewal',
  enterprise: 'Enterprise enquiry',
  pos: 'More POS outlets',
  pos_renew: 'POS renewal',
};

const STATUS_COLOR: Record<string, 'warning' | 'info' | 'default'> = { open: 'warning', contacted: 'info', closed: 'default' };

const PlatformRequests: FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState('open');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ status: 'contacted', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    platformApi.get(`/requests${status ? `?status=${status}` : ''}`)
      .then((response) => { setRows(response.data.requests); setError(''); })
      .catch((err) => setError(errorMessage(err)));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({ status: row.status === 'open' ? 'contacted' : row.status, notes: row.notes || '' });
  };

  const save = async () => {
    setSaving(true);
    try {
      await platformApi.put(`/requests/${editing._id}`, form);
      setEditing(null);
      load();
      window.dispatchEvent(new Event(REQUESTS_CHANGED_EVENT));
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      <Tabs value={status} onChange={(e, v) => setStatus(v)} sx={{ mt: -2, mb: 2, minHeight: 40, borderBottom: '1px solid #dcdcdc', '& .MuiTab-root': { minHeight: 40, py: 1 } }}>
        <Tab value='open' label='Open' sx={{ textTransform: 'none' }} />
        <Tab value='contacted' label='Contacted' sx={{ textTransform: 'none' }} />
        <Tab value='closed' label='Closed' sx={{ textTransform: 'none' }} />
        <Tab value='' label='All' sx={{ textTransform: 'none' }} />
      </Tabs>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #dcdcdc', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ bgcolor: '#e9ecf1', '& th': { fontWeight: 600, color: '#111827', borderBottom: '1px solid #dcdcdc', py: 1.25, whiteSpace: 'nowrap' } }}>
                <TableCell>Received</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Request</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell>
                    {row.account ? <Link to={`/platform/accounts/${row.account._id}`} style={{ color: '#059669', textDecoration: 'none' }}>{row.account.name}</Link> : '-'}
                    <div style={{ color: '#6b7280', fontSize: 12 }}>Now on {planLabel(row.account?.planCode)}</div>
                  </TableCell>
                  <TableCell>
                    {TYPE_LABEL[row.type] || row.type}
                    {row.planCode && <div style={{ color: '#6b7280', fontSize: 12 }}>Wants {planLabel(row.planCode)}</div>}
                  </TableCell>
                  <TableCell>
                    {row.contact?.name || '-'}
                    <div style={{ color: '#6b7280', fontSize: 12 }}>{[row.contact?.email, row.contact?.phone].filter(Boolean).join(' · ')}</div>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>{row.message || '-'}</TableCell>
                  <TableCell>
                    <Chip size='small' variant='outlined' color={STATUS_COLOR[row.status]} label={row.status[0].toUpperCase() + row.status.slice(1)} />
                    {row.handledBy && <div style={{ color: '#6b7280', fontSize: 12 }}>{row.handledBy}</div>}
                  </TableCell>
                  <TableCell align='right'>
                    <Button size='small' variant='outlined' onClick={() => openEdit(row)} sx={{ textTransform: 'none', height: 28 }}>Update</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={7}>No requests.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ px: 2, py: 1.5, fontSize: 13, color: '#374151' }}>Total: <b>{rows.length}</b> request(s)</Box>
      </Box>

      {editing && (
        <Dialog open onClose={() => setEditing(null)} maxWidth='sm' fullWidth>
          <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>Update request · {editing.account?.name}</DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            <TextField select label='Status' size='small' fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} sx={{ mb: 2 }}>
              <MenuItem value='open'>Open</MenuItem>
              <MenuItem value='contacted'>Contacted</MenuItem>
              <MenuItem value='closed'>Closed</MenuItem>
            </TextField>
            <TextField label='Notes' size='small' fullWidth multiline minRows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} helperText='e.g. "Called, sending quote for Pro yearly"' />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button size='small' variant='outlined' onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <LoadingButton size='small' variant='contained' loading={saving} onClick={save} sx={{ textTransform: 'none' }}>Save</LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default PlatformRequests;
